import { CardInformationRow } from '@ingenico/react-ui/card-information-row';
import { InformationCardContainer } from '@ingenico/react-ui/information-card-container';
import { InformationCardTitle } from '@ingenico/react-ui/information-card-title';
import { InformationRowContainer } from '@ingenico/react-ui/information-row-container';
import classNames from 'classnames';
import type { ReactElement } from 'react';

import { useCurrentUser } from '@ingenico/react-section-utils';
import { CoreChip } from '@ingenico/react-ui/core-chip';
import { DateTime } from '@ingenico/react-ui/date-time';
import { toString } from 'lodash-es';
import type { CallDetailsDto } from '../../model/callLogs.model';
import type { UseDeviceCallLogPageOutput } from '../../pages/device-call-log-page/useDeviceCallLogPage.vm';
import styles from './CallDetailsInformation.module.scss';

export interface CallDetailsInformationProps extends Pick<UseDeviceCallLogPageOutput, 't'> {
  readonly callDetails: CallDetailsDto;
}

export function CallDetailsInformation({ callDetails, t }: CallDetailsInformationProps): ReactElement {
  const currentUser = useCurrentUser();
  return (
    <InformationCardContainer>
      <InformationCardTitle title={toString(t('cardTitle.callDetails'))} />
      <div className={classNames(styles.bodyContainer)}>
        <div className={classNames(styles.sideContainer)}>
          <InformationRowContainer>
            <CardInformationRow
              isAllowCopyClipboard
              label={toString(t('label.callDetails.callId'))}
              value={callDetails.id}
            />
            <CardInformationRow label={toString(t('label.callDetails.callDate'))}>
              <DateTime
                instant={callDetails.date}
                timezone={currentUser.zoneInfo}
                userDateTimeFormat={currentUser.dateTimeFormat}
              />
            </CardInformationRow>
            <CardInformationRow
              isAllowCopyClipboard
              label={toString(t('label.callDetails.virtualDeviceId'))}
              value={callDetails.virtualDeviceId}
            />
            <CardInformationRow label={toString(t('label.callDetails.callingApp'))} value={callDetails.callingApp} />
            <CardInformationRow label={toString(t('label.callDetails.action'))}>
              <CoreChip
                className={classNames({
                  [styles.successChip]: callDetails.action.color === 'success',
                  [styles.errorChip]: callDetails.action.color === 'failed',
                  [styles.warningChip]: callDetails.action.color === 'warning',
                  [styles.neutralChip]: callDetails.action.color === 'neutral',
                  [styles.infoChip]: callDetails.action.color === 'info',
                })}
                label={toString(t(`values.application.${callDetails.action.label}`))}
              />
            </CardInformationRow>
          </InformationRowContainer>
        </div>
        <div className={classNames(styles.sideContainer)}>
          <InformationRowContainer>
            <CardInformationRow
              label={toString(t('label.callDetails.callReason'))}
              value={t(`values.callReason.${toString(callDetails.reason)}`, callDetails.reason)}
            />
            <CardInformationRow label={toString(t('label.callDetails.duration'))} value={callDetails.duration} />
            <CardInformationRow
              isAllowCopyClipboard
              label={toString(t('label.callDetails.virtualReference'))}
              value={callDetails.virtualReference}
            />
          </InformationRowContainer>
        </div>
      </div>
      <CardInformationRow label={toString(t('label.callDetails.summary'))} value={callDetails.summary} />
    </InformationCardContainer>
  );
}
